import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGetVenues, apiCreateVenue, apiUpdateVenue, apiSearchVenues, apiGetVenueContacts, type VenueDTO, type GeocodeResult } from '../api';
import AutoTextarea from '../components/AutoTextarea';
import VenueFilesSection from '../components/VenueFilesSection';
import PageHeader from '../components/PageHeader';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';

function emptyVenue(): VenueDTO {
  return { id: '', name: '', address: '', city: '', country: '', zip: '', state: '', capacity: undefined, notes: '', lat: 0, lng: 0 } as VenueDTO;
}

export default function VenueEditPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();

  const [venue, setVenue] = useState<VenueDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // geocode / venue-search state. Both the geocoder (GeocodeResult) and the
  // venue-search endpoint (VenueDTO) populate this dropdown — they share `name`
  // and coordinates (GeocodeResult uses `lon`, VenueDTO uses `lng`).
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<(VenueDTO | GeocodeResult)[]>([]);
  const [searching, setSearching] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  // Snapshot of the last persisted venue (server round-trip), used to skip the
  // debounced autosave when nothing changed since load/save — prevents a
  // redundant PUT right after load and any wipe-on-load-error (2026-08-26).
  const lastSavedRef = useRef<string>('');

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    apiGetVenues()
      .then((vs) => {
        if (cancelled) return;
        if (isNew) {
          setVenue(emptyVenue());
        } else {
          const existing = vs.find((v) => String(v.id) === String(id)) || vs.find((v) => v.slug === id);
          setVenue(existing || emptyVenue());
          // Snapshot what we actually loaded so the autosave below skips the
          // redundant PUT and never wipes real data on a transient load error.
          if (existing) lastSavedRef.current = JSON.stringify(existing);
        }
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) { setLoading(false); setLoadError(true); }
      });
    return () => { cancelled = true; };
  }, [id, isNew]);

  // ---- Autosave (debounced) ----
  // Editing any field (name, address, city, country, capacity, phone, email,
  // website, timezone, electricity, currency, stage_size, notes…) persists
  // automatically via apiUpdateVenue after a 500ms debounce, like the team
  // pages. The "Save Changes" button remains as an immediate save + navigate.
  useEffect(() => {
    if (isNew || !venue?.id) return;
    if (!venue.name?.trim()) return;
    const snap = JSON.stringify(venue);
    if (snap === lastSavedRef.current) return;
    const timer = setTimeout(async () => {
      try {
        const saved = await apiUpdateVenue(venue.id as string, venue);
        lastSavedRef.current = JSON.stringify(saved);
        reportSaveOk('venue');
      } catch (err) {
        reportSaveError('venue', err);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [venue, isNew]);

  // dynamic search (debounced Nominatim via backend)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim();
    if (q.length < 3) { setResults([]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      apiSearchVenues(q)
        .then((r) => { setResults(r); setSearching(false); })
        .catch(() => { setResults([]); setSearching(false); });
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  // close dropdown on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setResults([]);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleChange = useCallback(<K extends keyof VenueDTO>(field: K, value: VenueDTO[K]) => {
    setVenue((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  const pickResult = async (r: VenueDTO | GeocodeResult) => {
    const g = r as GeocodeResult;
    const v = r as VenueDTO;
    const contacts = await apiGetVenueContacts((g as any).osm_type, (g as any).osm_id).catch(() => ({}));
    setVenue((prev) => (prev ? {
      ...prev,
      name: prev.name?.trim() ? prev.name : (String(r.name || '').split(',')[0] || prev.name || ''),
      address: (g as any).address || prev.address || '',
      city: (g as any).city || prev.city || '',
      state: (g as any).state || prev.state || '',
      country: (g as any).country || prev.country || '',
      zip: (g as any).zip || prev.zip || '',
      lat: (g as any).lat ?? (v as any).lat ?? prev.lat ?? 0,
      // The venue-search endpoint returns `lng` (VenueDTO). Read it first,
      // fall back to `lon` (GeocodeResult shape) for robustness.
      lng: (v as any).lng ?? (g as any).lon ?? prev.lng ?? 0,
      // Derived day-sheet fields (from the venue's country, via online lookup)
      timezone: (g as any).timezone || prev.timezone || '',
      electricity: (g as any).electricity || prev.electricity || '',
      currency: (g as any).currency || prev.currency || '',
      // Contacts auto-filled from the online lookup
      phone: (contacts as any).phone || prev.phone || '',
      email: (contacts as any).email || prev.email || '',
      website: (contacts as any).website || prev.website || '',
    } : prev));
    setResults([]);
    setSearch('');
  };

  // Geocode the venue's own address → restore coordinates (so the map shows)
  // even on existing venues that never got lat/lng. Reuses the search endpoint
  // (Nominatim → Photon fallback) and pickResult to fill coords + tech fields.
  const setCoordsFromAddress = async () => {
    if (!venue || geocoding) return;
    const parts = [venue.name, venue.address, venue.city, venue.state, venue.country]
      .map((p) => (p || '').trim())
      .filter(Boolean);
    if (parts.length === 0) {
      alert(t('venueEdit.noAddress'));
      return;
    }
    setGeocoding(true);
    try {
      const res = await apiSearchVenues(parts.join(', '));
      if (!res || res.length === 0) {
        alert(t('venueEdit.noLocation'));
        return;
      }
      const city = (venue.city || '').toLowerCase();
      const best = (city && res.find((r) => (r.city || '').toLowerCase() === city)) || res[0];
      await pickResult(best as unknown as GeocodeResult);
    } catch (e) {
      console.error('geocode venue address failed', e);
      alert(t('venueEdit.geocodeFailed'));
    } finally {
      setGeocoding(false);
    }
  };

  const handleSave = async () => {
    if (!venue) return;
    if (!venue.name || !venue.name.trim()) {
      alert(t('venueEdit.enterName'));
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await apiCreateVenue(venue);
      } else {
        await apiUpdateVenue(venue.id as string, venue);
      }
      navigate('/tour/venues');
    } catch (err) {
      reportSaveError('venue', err);
      alert(t('venueEdit.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const lat = Number(venue?.lat) || 0;
  const lng = Number(venue?.lng) || 0;
  const hasCoords = lat !== 0 && lng !== 0;
  const mapSrc = hasCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01}%2C${lat - 0.01}%2C${lng + 0.01}%2C${lat + 0.01}&layer=mapnik&marker=${lat}%2C${lng}`
    : null;

  if (loadError) {
    return (
      <div className="page" style={{ padding: '48px', textAlign: 'center' }}>
        <p style={{ color: 'var(--danger)', marginBottom: 12 }}>{t('venueEdit.loadFailed')}</p>
        <button className="btn btn-secondary" data-edit-back onClick={() => navigate('/tour/venues')}>
          {t('venueEdit.backToList')}
        </button>
      </div>
    );
  }

  if (loading || !venue) {
    return (
      <div className="page" style={{ padding: '48px', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-dim)' }}>{t('venueEdit.loading')}</p>
      </div>
    );
  }

  return (
    <div className="page musician-edit-page" data-edit-page="venue" data-density="socle">
      <PageHeader
        title={isNew ? t('venueEdit.newVenue') : t('venueEdit.editTitle', { name: venue.name || t('venueEdit.unnamed') })}
        actions={
          <button className="btn btn-secondary" data-edit-back onClick={() => navigate('/tour/venues')}>
            {t('venueEdit.backToList')}
          </button>
        }
      />

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* LEFT: form */}
        <div className="edit-form" style={{ flex: '1 1 420px', minWidth: 0 }}>
          <div className="form-section">
            <h2>{t('venueEdit.venueInfo')}</h2>

            {isNew && (
            <div className="form-group" style={{ position: 'relative' }} ref={searchBoxRef}>
              <label>{t('venueEdit.searchPlace')}</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('venueEdit.searchPh')}
              />
              {searching && <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)' }}>{t('venueEdit.searching')}</div>}
              {results.length > 0 && (
                <ul className="rc-suggest">
                  {results.map((r, i) => (
                    <li key={i}>
                      <button type="button" onClick={() => pickResult(r)}>
                        {r.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            )}

            <div className="form-row rc-form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.name')}</label>
                <input type="text" data-edit-field="name" maxLength={160} value={venue.name || ''} onChange={(e) => handleChange('name', e.target.value)} placeholder={t('venueEdit.namePh')} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.capacity')}</label>
                <input type="number" data-edit-field="capacity" value={venue.capacity ?? ''} onChange={(e) => handleChange('capacity', e.target.value ? parseInt(e.target.value) : undefined)} placeholder={t('venueEdit.capacityPh')} />
              </div>
            </div>

            <div className="form-row rc-form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.address')}</label>
                <input type="text" value={venue.address || ''} onChange={(e) => handleChange('address', e.target.value)} placeholder={t('venueEdit.streetPh')} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.city')}</label>
                <input type="text" value={venue.city || ''} onChange={(e) => handleChange('city', e.target.value)} placeholder={t('venueEdit.cityPh')} />
              </div>
            </div>

            <div className="form-row rc-form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.zip')}</label>
                <input type="text" value={venue.zip || ''} onChange={(e) => handleChange('zip', e.target.value)} placeholder={t('venueEdit.zipPh')} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.stateRegion')}</label>
                <input type="text" value={venue.state || ''} onChange={(e) => handleChange('state', e.target.value)} placeholder={t('venueEdit.statePh')} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.country')}</label>
                <input type="text" value={venue.country || ''} onChange={(e) => handleChange('country', e.target.value)} placeholder={t('venueEdit.countryPh')} />
              </div>
            </div>

            <div className="form-row rc-form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.phone')}</label>
                <input type="text" value={venue.phone || ''} onChange={(e) => handleChange('phone', e.target.value)} placeholder={t('venueEdit.phonePh')} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.email')}</label>
                <input type="text" value={venue.email || ''} onChange={(e) => handleChange('email', e.target.value)} placeholder={t('venueEdit.emailPh')} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.website')}</label>
                <input type="text" value={venue.website || ''} onChange={(e) => handleChange('website', e.target.value)} placeholder={t('venueEdit.websitePh')} />
              </div>
            </div>

            <div className="form-row rc-form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.timezone')}</label>
                <input type="text" value={venue.timezone || ''} onChange={(e) => handleChange('timezone', e.target.value)} placeholder={t('venueEdit.timezonePh')} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.electricity')}</label>
                <input type="text" value={venue.electricity || ''} onChange={(e) => handleChange('electricity', e.target.value)} placeholder={t('venueEdit.electricityPh')} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.currency')}</label>
                <input type="text" value={venue.currency || ''} onChange={(e) => handleChange('currency', e.target.value)} placeholder={t('venueEdit.currencyPh')} />
              </div>
            </div>

            <div className="form-row rc-form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('venueEdit.stageSize')}</label>
                <input type="text" value={venue.stage_size || ''} onChange={(e) => handleChange('stage_size', e.target.value)} placeholder={t('venueEdit.stageSizePh')} />
                <span className="text-dim" style={{ fontSize: 'var(--font-xs)' }}>{t('venueEdit.stageSizeHint')}</span>
              </div>
            </div>

            <div className="form-group">
              <label>{t('venueEdit.notes')}</label>
              <AutoTextarea rows={3} value={venue.notes || ''} onChange={(e) => handleChange('notes', e.target.value)} placeholder={t('venueEdit.notesPh')} />
            </div>

            <div className="form-group">
              <label>{t('venueEdit.parking')}</label>
              <AutoTextarea rows={3} value={venue.parking || ''} onChange={(e) => handleChange('parking', e.target.value)} placeholder={t('venueEdit.parkingPh')} />
              <span className="text-dim" style={{ fontSize: 'var(--font-xs)' }}>{t('venueEdit.parkingHint')}</span>
            </div>
          </div>

          {/* Files: venue tech sheets / plans / photos (shared catalog) */}
          {!isNew && venue?.id && (
            <div style={{ marginTop: 20 }}>
              <h3 style={{ fontSize: 'var(--font-md)', margin: '0 0 8px' }}>{t('venueEdit.venueFiles')}</h3>
              <VenueFilesSection venueId={String(venue.id)} />
            </div>
          )}

          <div className="form-actions">
            <button className="btn btn-secondary" data-edit-back onClick={() => navigate('/tour/venues')}>
              {t('venueEdit.cancel')}
            </button>
            <button className="btn btn-primary" data-edit-save onClick={handleSave} disabled={saving}>
              {saving ? t('venueEdit.saving') : isNew ? t('venueEdit.create') : t('venueEdit.saveChanges')}
            </button>
          </div>
        </div>

        {/* RIGHT: free map (OSM embed, no API key) */}
        <div style={{ flex: '1 1 360px', minWidth: 0 }}>
          <h2 style={{ fontSize: 'var(--font-lg)' }}>{t('venueEdit.location')}</h2>
          {mapSrc ? (
            <iframe
              title="venue-map"
              width="100%"
              height="320"
              style={{ border: '1px solid var(--border)', borderRadius: 8 }}
              src={mapSrc}
              loading="lazy"
            />
          ) : (
            <div style={{ border: '1px dashed var(--border)', borderRadius: 8, padding: 24, textAlign: 'center', color: 'var(--text-dim)' }}>
              <Trans i18nKey="venueEdit.noCoords" components={{ br: <br /> }} />
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn btn-ghost btn-small"
              onClick={setCoordsFromAddress}
              disabled={geocoding || !venue}
            >
              {geocoding ? t('venueEdit.geocoding') : t('venueEdit.setCoords')}
            </button>
          </div>
          {hasCoords && (
            <p style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)', marginTop: 6 }}>
              <a href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=15/${lat}/${lng}`} target="_blank" rel="noreferrer">
                {t('venueEdit.openOsm')}
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}